// useTour hook - manages tour state and Supabase persistence
import { useState, useEffect, useCallback } from 'react';
import { useSession } from '../session/useSession';
import { supabase } from '../utils/supabaseClient';
import type { TabValue } from '../tours/types';

export interface TourState {
  completedTours: TabValue[];
  hasSeenWelcome: boolean;
  lastTourDate: string | null;
}

const DEFAULT_TOUR_STATE: TourState = {
  completedTours: [],
  hasSeenWelcome: false,
  lastTourDate: null,
};

export function useTour() {
  const { user } = useSession();
  const [tourState, setTourState] = useState<TourState>(DEFAULT_TOUR_STATE);
  const [activeTour, setActiveTour] = useState<TabValue | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasAttemptedAutoStart, setHasAttemptedAutoStart] = useState(false);

  // Load tour state from Supabase profiles.tour_state
  useEffect(() => {
    async function loadTourState() {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('tour_state')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (data?.tour_state) {
          setTourState(data.tour_state as TourState);
        }
      } catch (error) {
        console.error('Failed to load tour state:', error);
      } finally {
        setLoading(false);
      }
    }

    loadTourState();
  }, [user?.id]);

  // Save tour state to Supabase
  const saveTourState = useCallback(
    async (newState: TourState) => {
      if (!user?.id) return;

      try {
        const { error } = await supabase
          .from('profiles')
          .update({ tour_state: newState })
          .eq('id', user.id);

        if (error) throw error;

        setTourState(newState);
      } catch (error) {
        console.error('Failed to save tour state:', error);
      }
    },
    [user?.id]
  );

  // Start tour for a specific tab
  const startTour = useCallback(
    (tab: TabValue) => {
      setActiveTour(tab);
      setIsRunning(true);
      setHasAttemptedAutoStart(true);
    },
    []
  );

  // Mark tour as completed
  const completeTour = useCallback(
    async (tab: TabValue) => {
      const newState: TourState = {
        ...tourState,
        completedTours: [...new Set([...tourState.completedTours, tab])],
        hasSeenWelcome: true,
        lastTourDate: new Date().toISOString(),
      };

      await saveTourState(newState);
      setIsRunning(false);
      setActiveTour(null);
    },
    [tourState, saveTourState]
  );

  // Skip/close tour without marking complete
  const skipTour = useCallback(() => {
    setIsRunning(false);
    setActiveTour(null);
  }, []);

  // Reset all tours (for testing or user preference)
  const resetTours = useCallback(async () => {
    await saveTourState(DEFAULT_TOUR_STATE);
    setIsRunning(false);
    setActiveTour(null);
  }, [saveTourState]);

  // Check if a specific tour has been completed
  const isTourCompleted = useCallback(
    (tab: TabValue) => {
      return tourState.completedTours.includes(tab);
    },
    [tourState]
  );

  // Auto-start tour for new users on first visit (only once per session)
  const shouldAutoStartTour = useCallback(
    (tab: TabValue) => {
      return !hasAttemptedAutoStart && !tourState.hasSeenWelcome && !isTourCompleted(tab);
    },
    [hasAttemptedAutoStart, tourState, isTourCompleted]
  );

  return {
    tourState,
    activeTour,
    isRunning,
    loading,
    startTour,
    completeTour,
    skipTour,
    resetTours,
    isTourCompleted,
    shouldAutoStartTour,
  };
}
